import React, { useContext, useMemo, useState } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import { Spinner } from 'src/components/spinner';
import InfiniteScroll from 'src/components/infinite-scroll';
import Checkboxes from './components/checkboxes';
import RadioButtons from './components/radio-buttons';
import Section from '../section';
import { ListSearch } from './components/list-search';
import { SelectedFilters } from './components/selected-filters';
import { useAggregationSettings } from 'src/areas/product-collection/hooks';

import { ListFilterItem } from 'src/components/filter-section/types';

import * as S from './styles';

type ListItem = {
  id: string;
  value: string;
  count: number;
  path?: string | null;
  indexable?: boolean | null;
};

type Props = {
  autoFocusSearch?: boolean;
  getSelectedState: (id: string) => boolean;
  hasMore?: boolean;
  id: string;
  items: ListItem[];
  popular?: ListItem[];
  multiple?: boolean | null;
  onClickItem: (
    itemId: string,
    type: 'list',
    values: (string | number)[]
  ) => void;
  onLoadMoreItems?: () => void;
  property?: string;
  search?: boolean | null;
  selectedItems?: ListFilterItem[];
  showTitle?: boolean;
  title: string;
  useInfiniteScroll?: boolean;
};

export const List = ({
  id,
  title,
  multiple = false,
  hasMore = false,
  items,
  popular,
  property,
  search = false,
  useInfiniteScroll,
  selectedItems,
  showTitle,
  autoFocusSearch,
  getSelectedState,
  onClickItem,
  onLoadMoreItems,
}: Props) => {
  const { t } = useContext(AppContext);
  const aggregationSettings = useAggregationSettings(id);

  const [searchQuery, setSearchQuery] = useState(
    // Set to undefined to not perform a search on mount
    aggregationSettings?.query || undefined
  );

  const handleOnClick = (itemId: string, value: string) => {
    onClickItem(itemId, 'list', [value]);
  };

  const loader = useMemo(
    () =>
      hasMore ? (
        <S.LoaderWrapper>
          <Spinner position="none" size={24} isActive />
        </S.LoaderWrapper>
      ) : null,
    [hasMore]
  );

  const renderList = (listItems: ListItem[]) =>
    multiple ? (
      <Checkboxes
        items={listItems}
        getSelectedState={getSelectedState}
        onClickItem={handleOnClick}
      />
    ) : (
      <RadioButtons
        items={listItems}
        getSelectedState={getSelectedState}
        onClickItem={handleOnClick}
      />
    );

  const renderContent = () => {
    const showPopularItems =
      popular?.length && !searchQuery && search !== false;
    const showDivider =
      !searchQuery && popular?.length && items.length > 5 && search !== false;
    const showItems =
      searchQuery ||
      !popular?.length ||
      (popular?.length && items.length > 5) ||
      search === false;

    return (
      <S.OptionList data-test="OptionList">
        {showPopularItems ? renderList(popular) : undefined}
        {showDivider ? <S.Divider /> : undefined}
        {showItems ? renderList(items) : undefined}
        {hasMore && !useInfiniteScroll && (
          <S.OptionListItem key="show-more">
            <S.Option onClick={onLoadMoreItems}>
              {t('filter', 'properties.quantity-show-more')}
            </S.Option>
          </S.OptionListItem>
        )}
      </S.OptionList>
    );
  };

  const component = (
    <Section property={property} title={title} showTitle={showTitle}>
      {// If we had a totalCount from the API, we could just check totalCount > 20 here.
      // This tries to emulate that condition, but it's not bulletproof.
      (hasMore || items.length > 20 || searchQuery !== undefined) &&
        selectedItems && (
          <SelectedFilters
            getSelectedState={getSelectedState}
            handleOnClick={handleOnClick}
            selectedItems={selectedItems}
          />
        )}
      {search && (
        <ListSearch
          autoFocusSearch={autoFocusSearch}
          id={id}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          title={title}
        />
      )}
      {renderContent()}
    </Section>
  );

  return useInfiniteScroll && onLoadMoreItems ? (
    <InfiniteScroll
      onLoad={onLoadMoreItems}
      contentIndex={items.length}
      placeholder={loader}
    >
      {component}
    </InfiniteScroll>
  ) : (
    component
  );
};
